import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, Building2, Calendar, Zap } from "lucide-react";
import { ModalForm } from "@/components/ModalForm";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { X } from "lucide-react";


import { ExperienceEntry } from "@/utils/experienceStorage";

interface AddExperienceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (experience: ExperienceEntry) => void;
  isLoading?: boolean;
  editingExperience?: ExperienceEntry | null;
  editingIndex?: number | null;
}

export const AddExperienceModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  editingExperience = null,
  editingIndex = null
}: AddExperienceModalProps) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<ExperienceEntry>({
    role: "",
    dates: "",
    company: "",
    skills: ""
  });
  
  const [skillsArray, setSkillsArray] = useState<string[]>([]);
  const [currentSkill, setCurrentSkill] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isCurrentPosition, setIsCurrentPosition] = useState(false);

  // Initialize form data when editing
  useEffect(() => {
    if (editingExperience && isOpen) {
      setFormData(editingExperience);
      setSkillsArray(editingExperience.skills ? editingExperience.skills.split(", ").filter(s => s.trim()) : []);
      
      // Parse dates from the existing format "MM/YYYY - MM/YYYY" or "MM/YYYY - Present"
      const datesParts = editingExperience.dates.split(" - ");
      if (datesParts.length === 2) {
        setStartDate(datesParts[0].trim());
        const endPart = datesParts[1].trim();
        if (endPart === "Present") {
          setIsCurrentPosition(true);
          setEndDate("");
        } else {
          setIsCurrentPosition(false);
          setEndDate(endPart);
        }
      }
    } else if (isOpen) {
      // Reset form for new entry
      setFormData({
        role: "",
        dates: "",
        company: "",
        skills: ""
      });
      setSkillsArray([]);
      setCurrentSkill("");
      setStartDate("");
      setEndDate("");
      setIsCurrentPosition(false);
    }
  }, [editingExperience, isOpen]);

  // Helper function to validate MM/YYYY format
  const isValidMMYYYY = (dateString: string): boolean => {
    if (!dateString) return false;
    const pattern = /^(0[1-9]|1[0-2])\/\d{4}$/;
    return pattern.test(dateString);
  };

  // Helper function to validate and format dates
  const formatDatesString = (): string => {
    if (!startDate) return "";
    
    const formattedEnd = isCurrentPosition ? "Present" : endDate;
    
    return `${startDate} - ${formattedEnd}`;
  };

  // Update dates in formData whenever start/end dates change
  const updateDatesInFormData = () => {
    const datesString = formatDatesString();
    setFormData(prev => ({
      ...prev,
      dates: datesString
    }));
  };

  const handleInputChange = (field: keyof ExperienceEntry, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStartDateChange = (value: string) => {
    // Format and validate MM/YYYY input
    const formatted = formatMMYYYYInput(value);
    setStartDate(formatted);
    // Update formData dates after state update
    setTimeout(() => {
      const datesString = formatDatesString();
      setFormData(prev => ({ ...prev, dates: datesString }));
    }, 0);
  };

  const handleEndDateChange = (value: string) => {
    // Format and validate MM/YYYY input
    const formatted = formatMMYYYYInput(value);
    setEndDate(formatted);
    // Update formData dates after state update
    setTimeout(() => {
      const datesString = formatDatesString();
      setFormData(prev => ({ ...prev, dates: datesString }));
    }, 0);
  };

  // Helper function to format and validate MM/YYYY input
  const formatMMYYYYInput = (value: string): string => {
    // Remove all non-digits
    const numbers = value.replace(/\D/g, '');
    
    // Limit to 6 digits (MMYYYY)
    const limited = numbers.slice(0, 6);
    
    if (limited.length === 0) return '';
    if (limited.length <= 2) return limited;
    
    // Add slash after month
    const month = limited.slice(0, 2);
    const year = limited.slice(2);
    
    // Validate month (01-12)
    const monthNum = parseInt(month);
    if (monthNum > 12) {
      return limited.slice(0, 1) + '/' + limited.slice(1);
    }
    
    return month + '/' + year;
  };

  // Validation helper for comparing MM/YYYY dates
  const isEndDateValid = () => {
    if (isCurrentPosition || !startDate || !endDate) return true;
    
    // Convert MM/YYYY to comparable format
    const parseMMYYYY = (dateStr: string) => {
      const [month, year] = dateStr.split('/');
      return new Date(parseInt(year), parseInt(month) - 1);
    };
    
    try {
      const startDateObj = parseMMYYYY(startDate);
      const endDateObj = parseMMYYYY(endDate);
      return endDateObj >= startDateObj;
    } catch {
      return true; // Allow if parsing fails
    }
  };

  const handleCurrentPositionToggle = (checked: boolean) => {
    setIsCurrentPosition(checked);
    if (checked) {
      setEndDate("");
    }
    // Update formData dates after state update
    setTimeout(() => {
      const datesString = formatDatesString();
      setFormData(prev => ({ ...prev, dates: datesString }));
    }, 0);
  };

  const addSkill = () => {
    if (currentSkill.trim() && !skillsArray.includes(currentSkill.trim())) {
      const newSkills = [...skillsArray, currentSkill.trim()];
      setSkillsArray(newSkills);
      setFormData(prev => ({
        ...prev,
        skills: newSkills.join(", ")
      }));
      setCurrentSkill("");
    }
  };

  const removeSkill = (skillToRemove: string) => {
    const newSkills = skillsArray.filter(skill => skill !== skillToRemove);
    setSkillsArray(newSkills);
    setFormData(prev => ({
      ...prev,
      skills: newSkills.join(", ")
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSkill();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.role.trim() || !formData.company.trim() || !startDate) {
      return;
    }
    
    // Ensure dates are properly formatted before submitting
    const finalDatesString = formatDatesString();
    const finalFormData = {
      ...formData,
      dates: finalDatesString
    };
    
    onSubmit(finalFormData);
    
    // Reset form
    setFormData({
      role: "",
      dates: "",
      company: "",
      skills: ""
    });
    setSkillsArray([]);
    setCurrentSkill("");
    setStartDate("");
    setEndDate("");
    setIsCurrentPosition(false);
  };

  const isFormValid = formData.role.trim() && formData.company.trim() && isValidMMYYYY(startDate) && (isCurrentPosition || isValidMMYYYY(endDate));

  const isEditMode = editingExperience !== null;

  return (
    <ModalForm
      title={isEditMode ? "Edit Experience" : "Add Experience"}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={isLoading ? (isEditMode ? "Updating..." : "Adding...") : (isEditMode ? "Update Experience" : "Add Experience")}
      cancelText="Cancel"
      submitDisabled={!isFormValid || isLoading}
      className="max-w-2xl"
    >
      {/* Visual Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-sm mb-4">
          <Briefcase className="h-8 w-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {isEditMode ? "Edit Work Experience" : "Add Work Experience"}
        </h3>
        <p className="text-gray-600 text-sm">
          {isEditMode ? "Update this position in the candidate's professional timeline" : "Add a new position to build the candidate's professional timeline"}
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Role Field */}
        <div className="space-y-2">
          <Label htmlFor="role" className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Briefcase className="h-4 w-4 text-gray-500" />
            Position Title
          </Label>
          <Input
            id="role"
            value={formData.role}
            onChange={(e) => handleInputChange("role", e.target.value)}
            placeholder="e.g., Senior Software Engineer, Marketing Manager"
            className="w-full"
            required
          />
        </div>

        {/* Company Field */}
        <div className="space-y-2">
          <Label htmlFor="company" className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Building2 className="h-4 w-4 text-gray-500" />
            Company
          </Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => handleInputChange("company", e.target.value)}
            placeholder="e.g., Google, Microsoft, Acme Corp"
            className="w-full"
            required
          />
        </div>

        {/* Employment Period */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Calendar className="h-4 w-4 text-gray-500" />
            Employment Period
          </Label>
          
          {/* Start Date */}
          <div className="space-y-2">
            <Label htmlFor="startDate" className="text-sm font-medium text-gray-600">
              Start Date
            </Label>
            <Input
              id="startDate"
              type="text"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              placeholder="MM/YYYY (e.g., 01/2024)"
              className="w-full"
              maxLength={7}
              required
            />
          </div>

          {/* Current Position Toggle */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="currentPosition"
              checked={isCurrentPosition}
              onCheckedChange={handleCurrentPositionToggle}
            />
            <Label 
              htmlFor="currentPosition" 
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              Current position
            </Label>
          </div>

          {/* End Date - only show if not current position */}
          {!isCurrentPosition && (
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-sm font-medium text-gray-600">
                End Date
              </Label>
              <Input
                id="endDate"
                type="text"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                placeholder="MM/YYYY (e.g., 12/2024)"
                className="w-full"
                maxLength={7}
                required={!isCurrentPosition}
              />
            </div>
          )}

          {/* Preview formatted dates */}
          {startDate && (
            <div className="p-2 bg-blue-50 rounded-sm border border-blue-200">
              <p className="text-sm text-blue-700 font-medium">
                Period: {formatDatesString() || "Enter dates to see preview"}
              </p>
              <p className="text-xs text-blue-500 mt-1">
                Format: MM/YYYY (Numbers only, auto-formatted)
              </p>
            </div>
          )}
        </div>

        {/* Skills Field */}
        <div className="space-y-2">
          <Label htmlFor="skills" className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Zap className="h-4 w-4 text-gray-500" />
            Skills & Technologies
          </Label>
          
          {/* Skills Tags Display */}
          {skillsArray.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-sm border">
              {skillsArray.map((skill, index) => (
                <Badge 
                  key={index}
                  variant="secondary" 
                  className="bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm px-3 py-1 flex items-center gap-1"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeSkill(skill)}
                    className="ml-1 hover:bg-blue-300 rounded-sm p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          
          {/* Skills Input */}
          <div className="flex gap-2">
            <Input
              id="skills"
              value={currentSkill}
              onChange={(e) => setCurrentSkill(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a skill and press Enter or comma"
              className="flex-1"
            />
            <Button
              type="button"
              onClick={addSkill}
              disabled={!currentSkill.trim()}
              variant="outline"
              size="sm"
              className="px-4"
            >
              Add
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Add skills one by one. Press Enter or comma to add each skill.
          </p>
        </div>

        {/* Example Preview */}
        {(formData.role || formData.company || startDate) && (
          <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-sm">
            <h4 className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Preview
            </h4>
            <div className="space-y-1">
              {formData.role && (
                <p className="font-semibold text-gray-900">{formData.role}</p>
              )}
              {formData.company && (
                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {formData.company}
                </p>
              )}
              {startDate && (
                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDatesString()}
                </p>
              )}
              {skillsArray.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {skillsArray.slice(0, 5).map((skill, index) => (
                    <Badge 
                      key={index}
                      variant="secondary" 
                      className="bg-blue-200 text-blue-800 text-xs px-2 py-0.5"
                    >
                      {skill}
                    </Badge>
                  ))}
                  {skillsArray.length > 5 && (
                    <Badge variant="secondary" className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5">
                      +{skillsArray.length - 5} more
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ModalForm>
  );
};
